'use client'

// Animation
import { motion } from 'framer-motion'
import {
  Container,
  fadeInUp,
  Section,
  Spotlight,
  staggerContainer,
} from 'design-studio'

// Layout
// UI components
import { SectionHeader } from '@/components/ui/section-header'
import { FeatureCard } from '@/components/ui/feature-card'
import Link from 'next/link'
import {
  CpuChipIcon,
  BoltIcon,
  BeakerIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  SignalIcon,
} from '@heroicons/react/24/outline'

const FEATURES = [
  {
    title: 'AI Reasoning Infrastructure',
    description:
      'ImandraX is foundational infrastructure for AI reasoning and AI governance. Its cloud-native architecture with HTTP API, Python libraries and MCP server allows direct integration into AI agents, enabling them to prove properties, find counterexamples and reason formally.',
    icon: <CpuChipIcon className="h-5 w-5 text-cyan-400" />,
    accent: 'border-cyan-500/30 hover:border-cyan-400/50',
  },
  {
    title: 'Automation',
    description:
      "Push-button verification designed for engineers, not just Ph.D. researchers. ImandraX's Boyer-Moore-style inductive waterfall handles most proofs fully automatically — and when conjectures are false, it synthesizes concrete counterexamples rather than leaving you stranded.",
    icon: <BoltIcon className="h-5 w-5 text-teal-400" />,
    accent: 'border-teal-500/30 hover:border-teal-400/50',
  },
  {
    title: 'Counterexamples',
    description:
      'ImandraX\'s reasoning engine is "complete for counterexamples" in a precise sense — including higher-order counterexamples that synthesize functions as witnesses. This goes far beyond what traditional SMT solvers can do.',
    icon: <BeakerIcon className="h-5 w-5 text-blue-400" />,
    accent: 'border-blue-500/30 hover:border-blue-400/50',
  },
  {
    title: 'Imandra Universe',
    description:
      'ImandraX is part of Imandra Universe (universe.imandra.ai), an ecosystem of reasoners and neurosymbolic AI integration. CodeLogician, our neuro-symbolic reasoning agent, shows how LLMs and formal reasoning combine to analyze software with mathematical precision.',
    icon: <SparklesIcon className="h-5 w-5 text-violet-400" />,
    accent: 'border-violet-500/30 hover:border-violet-400/50',
  },
  {
    title: 'Extensibility',
    description:
      'ImandraX can be customized for particular domains — from financial systems and regulatory compliance to avionics controllers and neural network verification. Its logic is built on OCaml, making models efficiently executable in production.',
    icon: <PuzzlePieceIcon className="h-5 w-5 text-indigo-400" />,
    accent: 'border-indigo-500/30 hover:border-indigo-400/50',
  },
  {
    title: 'Meet You Where You Are',
    description:
      'Whether you write IML in VS Code, orchestrate proofs from Python notebooks, run verification in CI pipelines, or invoke ImandraX from an AI assistant via MCP — the same reasoning engine is accessible from every context.',
    icon: <SignalIcon className="h-5 w-5 text-slate-400" />,
    accent: 'border-slate-500/30 hover:border-slate-400/50',
  },
]

export function Features() {
  return (
    <Section
      className="relative scroll-mt-32 overflow-hidden bg-slate-200/5 py-16 sm:pt-24 sm:pb-48 lg:pt-38 dark:bg-gray-900/5"
      id="features"
    >
      <Container className="max-w-9xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          pretitle="Features"
          title="Key Features"
          description="ImandraX is a cloud-native reasoning engine built for the AI era — foundational infrastructure for AI reasoning and governance that integrates directly into AI agents and automated workflows. Built on decades of research in automated reasoning, it brings mathematical rigor to complex software at scale."
          pretitleColor="text-cyan-500/80"
          className="relative z-10 mb-16"
        />

        <div className="relative mt-16">
          <Spotlight
            color="from-cyan-600/10"
            className="-top-300 -left-256 z-0 size-512"
          >
            <Spotlight
              color="from-teal-600/10"
              className="top-180 left-128 z-0 size-256"
            />
          </Spotlight>
          <Spotlight
            color="from-cyan-600/15"
            className="-right-160 -bottom-160 z-0 size-320"
          />

          <motion.div
            className="relative z-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInUp}
          >
            {FEATURES.map((feature, index) => (
              <FeatureCard
                key={index}
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                accent={feature.accent}
                delay={index * 0.08}
              />
            ))}
          </motion.div>
        </div>

        <motion.p
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-50px 0px' }}
          className="relative z-10 mx-auto mt-12 max-w-6xl text-lg text-slate-300"
        >
          Explore the full documentation{' '}
          <Link
            href="/docs/"
            className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
          >
            here.
          </Link>
        </motion.p>
      </Container>
    </Section>
  )
}
